import React, { useState } from 'react';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import Button from '../../components/Button';
import PlatefullAILogo from '../../images/PlatefullAILogoNB.svg';
import './DeveloperPortalLayout.css';

interface DocumentationProps {
  onNavigate: (section: string) => void;
}

const Documentation: React.FC<DocumentationProps> = ({ onNavigate }) => {
  const [selectedSection, setSelectedSection] = useState<string>('overview');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoStep, setDemoStep] = useState(1);

  const overviewSections = [
    { id: 'overview', name: 'Overview', description: 'Introduction to PlatefullAI integration' }
  ];

  const webIntegrationSections = [
    { id: 'web-getting-started', name: 'Getting Started', description: 'Register your app and get started' },
    { id: 'web-html-integration', name: 'HTML/CSS/JavaScript', description: 'Pure JavaScript integration guide' },
    { id: 'css-classes', name: 'CSS Classes', description: 'Available CSS classes and styling guide' },
    { id: 'venue-tracking', name: 'Venue Visit Tracking', description: 'Track online visits with venue IDs' }
  ];

  const mobileIntegrationSections = [
    { id: 'mobile-getting-started', name: 'Getting Started', description: 'Register your app and get started' },
    { id: 'react-native-sdk', name: 'React Native SDK', description: 'Complete SDK integration guide' }
  ];

  const renderCSSClasses = () => (
    <div className="integration-content">
      <h3>CSS Classes Reference</h3>
      <p>
        PlatefullAI automatically adds CSS classes to your menu items based on dietary analysis. 
        Style these classes to match your brand and provide clear visual feedback to users.
      </p>

      <div className="info-section">
        <h4>Ingredient-Level Classes</h4>
        <p>These classes are applied to individual ingredients within descriptions:</p>
        
        <div className="field-explanation">
          <h5>pfai-conflict</h5>
          <p>Applied to ingredients that conflict with the user's dietary preferences or restrictions.</p>
          <p><strong>Use case:</strong> Highlight allergens, non-vegan ingredients for vegans, gluten for gluten-free users</p>
      <div className="code-block">
            <pre><code>{`.pfai-conflict {
  color: #dc2626;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  padding: 2px 4px;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: underline wavy;
  text-decoration-color: #ef5350;
  cursor: help;
}`}</code></pre>
      </div>
      </div>

        <div className="field-explanation">
          <h5>pfai-match</h5>
          <p>Applied to ingredients that match the user's dietary preferences.</p>
          <p><strong>Use case:</strong> Highlight plant-based ingredients for vegans, high-protein items for fitness users</p>
        <div className="code-block">
            <pre><code>{`.pfai-match {
  border-bottom: 2.5px dotted #00aa07;
  cursor: help;
  transition: all 0.2s ease;
}

.pfai-match:hover {
  border-bottom-width: 3px;
  border-bottom-color: #00aa07;
}`}</code></pre>
        </div>
      </div>

        <div className="field-explanation">
          <h5>pfai-info-silent</h5>
          <p>Applied to ingredients with nutritional information (no preference match or conflict).</p>
          <p><strong>Use case:</strong> Provide subtle information for educational purposes</p>
        <div className="code-block">
            <pre><code>{`.pfai-info-silent {
  cursor: help;
  text-decoration: underline dotted;
  text-decoration-color: rgba(0, 0, 0, 0.3);
  text-decoration-thickness: 1px;
}

.pfai-info-silent:hover {
  text-decoration-color: rgba(0, 0, 0, 0.6);
}`}</code></pre>
          </div>
          </div>
      </div>

      <div className="info-section">
        <h4>Title-Level Classes</h4>
        <p>These classes are applied to menu item titles:</p>
        
        <div className="field-explanation">
          <h5>pfai-title-conflict</h5>
          <p>Applied to the entire dish name when it contains conflicting ingredients.</p>
        <div className="code-block">
            <pre><code>{`.pfai-title-conflict {
  color: rgba(211, 47, 47, 0.7);
}`}</code></pre>
      </div>
    </div>

        <div className="field-explanation">
          <h5>pfai-title-match</h5>
          <p>Applied to the entire dish name when it matches user preferences.</p>
        <div className="code-block">
            <pre><code>{`.pfai-title-match {
  color: rgba(22, 163, 74, 0.8);
  font-weight: 600;
}`}</code></pre>
        </div>
        </div>
      </div>

      <div className="info-section">
        <h4>Container-Level Classes</h4>
        <p>These classes are applied to the entire menu item container:</p>
        
        <div className="field-explanation">
          <h5>pfai-container-match</h5>
          <p>Applied to the entire menu item when it matches user preferences.</p>
        <div className="code-block">
            <pre><code>{`.pfai-container-match {
  background: linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%);
  position: relative;
  border-left: 4px solid #4caf50;
}

/* Optional: Add shimmer effect */
.pfai-container-match::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #4caf50, #8bc34a, #4caf50);
  background-size: 100% 200%;
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { background-position: 0% 0%; }
  50% { background-position: 0% 100%; }
}`}</code></pre>
        </div>
      </div>

        <div className="field-explanation">
          <h5>pfai-container-conflict</h5>
          <p>Applied to the entire menu item when it contains conflicting ingredients.</p>
        <div className="code-block">
            <pre><code>{`.pfai-container-conflict {
  opacity: 0.4;
  transition: opacity 0.3s ease;
}

.pfai-container-conflict:hover {
  opacity: 0.6;
}`}</code></pre>
          </div>
        </div>
      </div>

      <div className="info-section">
        <h4>Tooltip Attributes</h4>
        <p>PlatefullAI adds tooltip attributes to provide additional context:</p>
        <ul>
          <li><code>pfai-conflict-tooltip</code> - Explains why the ingredient conflicts</li>
          <li><code>pfai-match-tooltip</code> - Explains why the ingredient matches</li>
          <li><code>pfai-info-tooltip</code> - Provides nutritional or general information</li>
        </ul>
        <div className="code-block">
          <pre><code>{`/* Style tooltips */
.pfai-conflict::after,
.pfai-match::after,
.pfai-info-silent::after {
  content: attr(pfai-conflict-tooltip);
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  max-width: 200px;
  z-index: 10000;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.pfai-match::after {
  content: attr(pfai-match-tooltip);
}

.pfai-info-silent::after {
  content: attr(pfai-info-tooltip);
}

.pfai-conflict:hover::after,
.pfai-match:hover::after,
.pfai-info-silent:hover::after {
  opacity: 1;
}`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Customization Tips</h4>
        <ul>
          <li><strong>Brand Colors:</strong> Replace the default green/red colors with your brand palette</li>
          <li><strong>Subtle Approach:</strong> Reduce opacity and remove backgrounds for a minimal look</li>
          <li><strong>Bold Approach:</strong> Add borders, shadows, and animations for maximum visibility</li>
          <li><strong>Accessibility:</strong> Ensure sufficient color contrast (WCAG AA: 4.5:1 minimum)</li>
          <li><strong>Mobile:</strong> Use larger touch targets and clear indicators on small screens</li>
        </ul>
        </div>
      </div>
  );

  const renderOverview = () => (
    <div className="integration-content">
      <h3>Welcome to PlatefullAI Integration</h3>
      <p>
        PlatefullAI enables you to deliver fully personalized menu experiences for your customers with minimal effort. 
        Simply send us your menu items, and we return them enhanced with custom CSS classes based on each user's 
        dietary preferences, restrictions, and nutritional goals—you just style the CSS properties to match your design.
      </p>

      <div className="info-section">
        <h4>What You Can Do</h4>
        <ul>
          <li><strong>Flexible Markup:</strong> Mark up your container, title, and/or description fields—highlight entire menu items, specific ingredients, or both</li>
          <li><strong>Smart Tooltips:</strong> Automatic tooltip information displays on hover, showing users relevant details like protein levels, macros, and allergen warnings</li>
          <li><strong>Logged-In Personalization:</strong> Authenticated users see menus tailored to their specific dietary preferences and restrictions</li>
          <li><strong>General Information:</strong> Non-logged-in users still benefit from general dietary information and ingredient highlights</li>
          <li><strong>Zero Heavy Lifting:</strong> No complex logic required—we handle the dietary classification, you handle the styling</li>
        </ul>
      </div>

      <div className="info-section">
        <h4>How It Works</h4>
        <ol>
          <li>Send your menu items to our API</li>
          <li>We analyze and classify ingredients based on user preferences</li>
          <li>Receive your menu back with CSS classes applied</li>
          <li>Style the classes to match your brand</li>
        </ol>
      </div>

      <div className="info-section">
        <h4>Getting Started</h4>
        <p>
          Choose your integration method from the sidebar and view the getting started guide there.
        </p>
      </div>
    </div>
  );

  const renderWebGettingStarted = () => (
    <div className="integration-content">
      <h3>Getting Started with Web Integration</h3>
      <p>
        Welcome! Let's get you set up with PlatefullAI. The first step is to register your application 
        in the Developer Portal to obtain your API credentials.
      </p>

      <div className="info-section">
        <h4>Step 1: Register Your Application</h4>
        <p>Navigate to the "Register OAuth App" section in the Developer Portal and create a new application.</p>
      </div>

      <div className="info-section">
        <h4>Application Fields Explained</h4>
        
        <div className="field-explanation">
          <h5>Name</h5>
          <p>Give your application a descriptive name. This will be displayed to users during authentication.</p>
          <p><em>Example: "Bella's Bistro Menu", "Acme Restaurant POS"</em></p>
        </div>

        <div className="field-explanation">
          <h5>Description</h5>
          <p>A brief description of your application. This helps users understand what they're authorizing.</p>
          <p><em>Example: "Access your dietary preferences to personalize our menu"</em></p>
      </div>

        <div className="field-explanation">
          <h5>Redirect URIs</h5>
          <p>
            The URL(s) where we'll send users after they complete authentication. Format each URI like this:
          </p>
        <div className="code-block">
            <pre><code>https://yourdomain.com/auth/platefullai/callback</code></pre>
        </div>
          <p>
            We'll redirect users back to this URL with authentication information. This is where you should 
            handle the redirect and process the information we provide. You can add multiple redirect URIs 
            for different environments (e.g., development, staging, production).
          </p>
          <p>
            <strong>Important:</strong> The redirect URI is automatically used by the PlatefullAI widget based on your 
            registered OAuth application. You don't need to specify it in your code—just make sure the URLs you 
            register here match where your callback page is hosted.
          </p>
      </div>

        <div className="field-explanation">
          <h5>Allowed URLs</h5>
          <p>
            The domain(s) that are authorized to make API calls to PlatefullAI. Add both your development 
            and production domains here.
          </p>
          <p><em>Examples:</em></p>
          <ul>
            <li><code>https://localhost:3000</code> (development)</li>
            <li><code>https://dev.yourrestaurant.com</code> (staging)</li>
            <li><code>https://yourrestaurant.com</code> (production)</li>
        </ul>
          <p>
            These domains will be validated when API requests are made to ensure they're coming from 
            authorized sources.
          </p>
        </div>
      </div>

      <div className="info-section">
        <h4>Step 2: Save Your Credentials</h4>
        <p>
          After registering your application, you'll receive a <strong>Client ID</strong> and 
          <strong>Client Secret</strong>. Store these securely—you'll need them to authenticate 
          your API requests.
        </p>
      </div>

      <div className="info-section">
        <h4>Next Steps</h4>
        <p>Once your app is registered, you're ready to start integrating! Check out the additional guides in this section for specific implementation details.</p>
      </div>
    </div>
  );

  const renderWebHTMLIntegration = () => (
    <div className="integration-content">
      <h3>HTML/CSS/JavaScript Integration</h3>
      <p>
        Integrate PlatefullAI into your website using pure HTML, CSS, and JavaScript. 
        This guide walks you through the complete integration process step by step.
      </p>

      <div className="info-section">
        <h4>Step 1: Load the PlatefullAI Widget Script</h4>
        <p>Add the PlatefullAI widget script to your HTML template:</p>
        <div className="code-block">
          <pre><code>{`<!-- base.html or your main layout file -->
<script src="https://platefullai.com/static/widget.js" 
        data-client-id="your_client_id_here">
</script>`}</code></pre>
    </div>
        <p>Replace <code>your_client_id_here</code> with your Client ID from the Register OAuth App section.</p>
      </div>

        <div className="info-section">
        <h4>Step 2: Add the Sign-In Button</h4>
        <p>Create a container for the sign-in button in your navigation:</p>
        <div className="code-block">
          <pre><code>{`<nav class="navbar">
  <div class="nav-menu">
    <a href="/" class="nav-link">Home</a>
    <a href="/menu" class="nav-link">Menu</a>
    
    <!-- PlatefullAI sign-in button container -->
    <div id="platefullai-signin"></div>
    </div>
</nav>`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Step 3: Render the Sign-In Button</h4>
        <p>Initialize the sign-in button with JavaScript:</p>
        <div className="code-block">
          <pre><code>{`function initializePlatefullAI() {
  if (typeof PlatefullAI === 'undefined') {
    console.error('PlatefullAI widget not loaded');
    return;
  }

  // Render the sign-in button
  PlatefullAI.renderSignInButton('platefullai-signin', {
    buttonText: 'Sign in with PlatefullAI',
    userEmail: localStorage.getItem('platefullai_user_email'),
    pulse: true,
    onSuccess: (user) => {
      localStorage.setItem('platefullai_user_email', user.email);
      console.log('User signed in:', user.email);
      window.location.reload();
    }
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initializePlatefullAI);`}</code></pre>
        </div>
        <p><strong>Note:</strong> The redirect URI is automatically determined from your registered OAuth application. No need to specify it here.</p>
      </div>

      <div className="info-section">
        <h4>Step 4: Handle OAuth Callback</h4>
        <p>Create a callback page to handle the OAuth redirect. Users will be sent here after signing in:</p>
        <div className="code-block">
          <pre><code>{`<!-- auth_callback.html -->
<!DOCTYPE html>
<html>
<head>
  <title>Authentication Successful</title>
</head>
<body>
  <h1>Authenticating with PlatefullAI...</h1>
  <p>Please wait while we complete your sign-in.</p>
  
  <script>
    // The PlatefullAI widget automatically handles the OAuth flow
    // Redirect back to home after a short delay
      setTimeout(() => {
      window.location.href = '/';
      }, 1000);
  </script>
</body>
</html>`}</code></pre>
        </div>
        
        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <button
            onClick={() => setShowDemoModal(true)}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🎬 Test This Flow (Interactive Demo)
          </button>
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            See exactly how the OAuth flow works with an interactive walkthrough
          </p>
        </div>
      </div>

      <div className="info-section">
        <h4>Step 5: Understanding PlatefullAI.processItems()</h4>
        <p>
          The <code>processItems()</code> method is the core of PlatefullAI integration. It analyzes your menu items 
          and returns marked-up HTML with CSS classes based on dietary preferences.
        </p>

        <div className="field-explanation">
          <h5>Required Parameters</h5>
          
          <div style={{ marginBottom: '20px' }}>
            <p><strong>menuItems</strong> (Array) - Your menu items to process</p>
            <div className="code-block">
              <pre><code>{`const menuItems = [
  {
    name: "Signature Burger",
    description: "Grilled <beef patty> with <cheddar>, <lettuce>, and house sauce",
    vegan: false,
    vegetarian: false,
    gluten_free: false
  },
  // ... more items
];`}</code></pre>
            </div>
      </div>

          <div style={{ marginBottom: '20px' }}>
            <p><strong>markup</strong> (Object) - Tells us which fields to mark up and what they're called</p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              We don't know how you've named your fields, so you need to tell us. You can specify up to three fields:
            </p>
            <ul style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
              <li><strong>name</strong> - Field name for your item's title (e.g., "name", "title", "dish_name")</li>
              <li><strong>description</strong> - Field name for your item's description (e.g., "description", "desc", "ingredients")</li>
              <li><strong>container</strong> - Field name for the container div (e.g., "menu-item", "dish-card")</li>
            </ul>
        <div className="code-block">
              <pre><code>{`markup: {
  name: 'name',           // We'll look for item.name
  description: 'description', // We'll look for item.description
  container: 'menu-item'  // We'll apply classes to this container
}`}</code></pre>
        </div>
      </div>
    </div>

        <div className="field-explanation">
          <h5>Optional Parameters</h5>
          
          <div style={{ marginBottom: '20px' }}>
            <p><strong>userEmail</strong> (String) - The authenticated user's email</p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              <strong>With userEmail:</strong> We'll provide <em>personalized</em> markup based on the user's dietary preferences, 
              allergens, and health concerns. Items will be marked with <code>pfai-conflict</code> or <code>pfai-match</code> classes.
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              <strong>Without userEmail:</strong> We'll provide <em>general nutritional highlighting</em> for all users. 
              Items will be marked with <code>pfai-info-silent</code> class and include general nutritional tooltips.
            </p>
        <div className="code-block">
              <pre><code>{`// Personalized for logged-in users
userEmail: "user@example.com"

// OR omit for general highlighting
// userEmail: undefined`}</code></pre>
        </div>
      </div>

          <div style={{ marginBottom: '20px' }}>
            <p><strong>useBrackets</strong> (Boolean) - How to identify ingredients in descriptions</p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              We support two formats for descriptions:
            </p>
            <div style={{ marginLeft: '20px', marginTop: '12px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>❌ Comma-separated (Limited)</p>
              <div className="code-block">
                <pre><code>{`description: "onion, bacon, ranch"`}</code></pre>
        </div>
              <p style={{ fontSize: '13px', color: '#999', marginTop: '8px' }}>
                <strong>Issue:</strong> Words like "and" cause problems: "onion, bacon, and ranch" would try to identify "and ranch" as an ingredient
              </p>
      </div>

            <div style={{ marginLeft: '20px', marginTop: '12px' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>✅ Angle Brackets (Recommended)</p>
        <div className="code-block">
                <pre><code>{`description: "Ooey gooey <parmesan cheese> sprinkled with fresh <herbs> and <garlic>"`}</code></pre>
        </div>
              <p style={{ fontSize: '13px', color: '#4ecdc4', marginTop: '8px' }}>
                <strong>Benefit:</strong> Full flexibility! Write descriptive, compelling descriptions while clearly marking which parts are ingredients
              </p>
      </div>

            <div className="code-block" style={{ marginTop: '16px' }}>
              <pre><code>{`useBrackets: true  // Enable angle bracket parsing (recommended)`}</code></pre>
            </div>
        </div>
      </div>

        <div className="field-explanation">
          <h5>Complete Example</h5>
        <div className="code-block">
            <pre><code>{`const result = await PlatefullAI.processItems({
  userEmail: userEmail,  // Personalized if provided
  menuItems: menuItems,
  useBrackets: true,     // Enable angle brackets
  markup: {
    name: 'name',
    description: 'description',
    container: 'menu-item'
  }
});

// Result contains marked-up HTML arrays
// We'll cover the output in Step 6`}</code></pre>
    </div>
        </div>
      </div>

      <div className="info-section">
        <h4>Step 6: Process Menu Items with PlatefullAI</h4>
        <p>Call the PlatefullAI API to process your menu and add CSS classes:</p>
        <div className="code-block">
          <pre><code>{`async function processMenuItems() {
  if (typeof PlatefullAI === 'undefined') {
    return;
  }

  // Get user email
  const userEmail = localStorage.getItem('platefullai_user_email');
  
  // Get all menu items
  const allItems = Array.from(document.querySelectorAll('.menu-item[data-description]'));
  
  if (allItems.length === 0) {
    return;
  }

  // Prepare menu items data
  const processedItems = allItems.map(item => {
    const nameElement = item.querySelector('.menu-title');
    const descElement = item.querySelector('.menu-description');
    
    // Extract dietary data from data attributes
    const dietaryData = {
      vegan: item.getAttribute('data-vegan') === 'true',
      vegetarian: item.getAttribute('data-vegetarian') === 'true',
      gluten_free: item.getAttribute('data-gluten-free') === 'true',
      nut_free: item.getAttribute('data-nut-free') === 'true'
    };
    
    return {
      name: nameElement?.textContent || '',
      description: item.getAttribute('data-description') || '',
      element: item,
      nameElement: nameElement,
      descElement: descElement,
      ...dietaryData
    };
  }).filter(item => item.description);

  try {
    // Process menu with PlatefullAI
    const result = await PlatefullAI.processMenu({
      userEmail: userEmail,
      menuItems: processedItems,
      useBrackets: true, // Enable angle bracket parsing
      markup: {
        name: 'name',
        description: 'description',
        container: 'menu-item'
      }
    });

    // Apply the processed results
    if (result && result.name && result.description) {
      processedItems.forEach((item, index) => {
        // Update description with PlatefullAI markup
        if (result.description[index] && item.descElement) {
          item.descElement.innerHTML = result.description[index];
        }
        
        // Update title with PlatefullAI markup
        if (result.name[index] && item.nameElement) {
          item.nameElement.innerHTML = result.name[index];
    }
  });
}
  } catch (error) {
    console.error('PlatefullAI processing error:', error);
  }
}

// Call this after DOM loads
document.addEventListener('DOMContentLoaded', processMenuItems);`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>What Happens Next</h4>
        <p>After processing, PlatefullAI will:</p>
        <ul>
          <li><strong>Add markup to ingredients</strong> in descriptions with CSS classes (<code>pfai-conflict</code>, <code>pfai-match</code>, <code>pfai-info-silent</code>)</li>
          <li><strong>Add container classes</strong> to menu items (<code>pfai-container-match</code>, <code>pfai-container-conflict</code>)</li>
          <li><strong>Add title classes</strong> for items with matches or conflicts (<code>pfai-title-match</code>, <code>pfai-title-conflict</code>)</li>
          <li><strong>Include tooltips</strong> via attributes that explain why ingredients match or conflict</li>
            </ul>
        <p>Check the <strong>CSS Classes</strong> tab in the Overview section for styling guidance.</p>
          </div>
          
      <div className="info-section">
        <h4>Complete HTML Example</h4>
        <p>Here's a basic working example to get you started:</p>
        <div className="code-block">
          <pre><code>{`<!DOCTYPE html>
<html>
<head>
  <title>My Restaurant Menu</title>
  <link rel="stylesheet" href="styles.css">
</head>
<body>
  <!-- Navigation with sign-in button -->
  <nav>
    <div id="platefullai-signin"></div>
  </nav>

  <!-- Menu Items -->
  <div id="menu">
    <div class="menu-item"
         data-name="Grilled Chicken"
         data-description="Grilled <chicken breast>, <romaine lettuce>, <caesar dressing>"
         data-vegan="false"
         data-vegetarian="false">
      <h3 class="menu-title">Grilled Chicken</h3>
      <p class="menu-description">
        Grilled <chicken breast>, <romaine lettuce>, <caesar dressing>
      </p>
      <span class="price">$14.99</span>
          </div>
          </div>
          
  <!-- Load PlatefullAI Widget -->
  <script src="https://platefullai.com/static/widget.js" 
          data-client-id="your_client_id_here">
  </script>

  <!-- Your JavaScript -->
  <script src="main.js"></script>
</body>
</html>`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Next Steps</h4>
        <ul>
          <li>Replace <code>your_client_id_here</code> with your actual Client ID from the Register OAuth App page</li>
          <li>Add your menu items with proper data attributes</li>
          <li>Style the PlatefullAI CSS classes (see the CSS Classes tab for examples)</li>
          <li>Test with different user accounts to see personalized highlighting</li>
        </ul>
      </div>
    </div>
  );

  const renderMobileGettingStarted = () => (
    <div className="integration-content">
      <h3>Getting Started with Mobile Integration</h3>
      <p>
        Welcome! Let's get you set up with PlatefullAI mobile integration. The first step is to register your application 
        in the Developer Portal to obtain your API credentials.
      </p>

      <div className="info-section">
        <h4>Step 1: Create Account</h4>
        <p>
          Go to the Developer Portal and create an account if you haven't already.
        </p>
      </div>

      <div className="info-section">
        <h4>Step 2: Register New Application</h4>
        <p>After creating your account, register a new OAuth application with the following fields:</p>
        
        <div className="field-explanation">
          <h5>App Name</h5>
          <p>A descriptive name for your application</p>
          <p><em>Example: "MobylMenu"</em></p>
        </div>

        <div className="field-explanation">
          <h5>Description</h5>
          <p>Brief description of what your app does</p>
          <p><em>Example: "A menu hosting app"</em></p>
        </div>

        <div className="field-explanation">
          <h5>Redirect URL</h5>
          <p>Where PlatefullAI will send users after they finish authenticating</p>
          <p><em>Example: <code>mobylmenu://oauth-callback</code></em></p>
        </div>

        <div className="field-explanation">
          <h5>Allowed Domains</h5>
          <p>Your development and production domains</p>
          <p><em>Examples:</em></p>
          <ul>
            <li><code>localhost</code> (for development)</li>
            <li><code>mobylmenu.com</code> (for production)</li>
          </ul>
        </div>
      </div>

      <div className="info-section">
        <h4>Example Configuration</h4>
        <div className="code-block">
          <pre><code>{`App Name: MobylMenu
Description: A menu hosting app for restaurants
Redirect URL: mobylmenu://oauth-callback
Allowed Domains:
  - localhost (for development)
  - mobylmenu.com (for production)`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Step 3: Create Your Venues</h4>
        <p>
          Before integrating the SDK, you'll need to set up your venues in the Developer Portal. 
          PlatefullAI provides two methods depending on your use case:
        </p>

        <div className="field-explanation">
          <h5>Option 1: Single Venue Creation (Single Restaurant Apps)</h5>
          <p>Perfect for individual restaurants or single-venue applications.</p>
          <ol>
            <li>Navigate to <strong>Venues & Locations</strong> in the Developer Portal</li>
            <li>Click <strong>"Create New Venue"</strong></li>
            <li>Fill in venue details:
              <ul>
                <li><strong>Name:</strong> Restaurant/venue name (required)</li>
                <li><strong>City:</strong> City location (optional)</li>
                <li><strong>State:</strong> State/Province (optional)</li>
                <li><strong>Country:</strong> Country (required)</li>
                <li><strong>Internal ID:</strong> Your internal identifier (optional but recommended)</li>
              </ul>
            </li>
            <li>Click <strong>"Create Venue"</strong></li>
          </ol>
        </div>

        <div className="field-explanation">
          <h5>Option 2: Bulk Venue Creation (Multi-Venue Apps)</h5>
          <p>
            <strong>Perfect for platforms like DoorDash, UberEats, or any app hosting multiple restaurants.</strong>
          </p>
          <p>Upload multiple venues at once using CSV:</p>
          <ol>
            <li>Navigate to <strong>Venues & Locations</strong> in the Developer Portal</li>
            <li>Click <strong>"Upload CSV"</strong></li>
            <li>Select your CSV file with venue data</li>
            <li>Choose action:
              <ul>
                <li><strong>Add new venues:</strong> Keep existing venues, add new ones</li>
                <li><strong>Replace all venues:</strong> Delete all existing venues, add new ones</li>
                <li><strong>Delete venues:</strong> Delete venues matching internal IDs in CSV</li>
              </ul>
            </li>
            <li>Preview data and click the action button</li>
          </ol>
          <div className="code-block">
            <pre><code>{`name,city,state,country,internal_id
Joe's Pizza,New York,NY,United States,venue_001
Maria's Tacos,Los Angeles,CA,United States,venue_002
Tony's Pasta,Chicago,IL,United States,venue_003`}</code></pre>
          </div>
          <p style={{ marginTop: '12px' }}>
            <strong>For Multi-Venue Apps:</strong> The <code>internal_id</code> field is critical. This should be the same 
            ID you use in your application to identify venues (e.g., your database venue ID, restaurant partner ID, etc.). 
            This allows PlatefullAI to track which venues your users visit and report that data back to you using identifiers 
            you already know.
          </p>
        </div>

        <div className="field-explanation">
          <h5>Multi-Venue App Benefits</h5>
          <ul>
            <li><strong>Scalability:</strong> Manage hundreds or thousands of restaurants easily</li>
            <li><strong>Visit Tracking:</strong> Track which partner restaurants your users visit</li>
            <li><strong>Seamless Integration:</strong> Use your existing venue IDs, no need to manage UUIDs</li>
            <li><strong>Analytics:</strong> Get insights into which venues your users prefer</li>
            <li><strong>Bulk Operations:</strong> Add, update, or remove venues in bulk</li>
          </ul>
        </div>
      </div>

      <div className="info-section">
        <h4>Next Steps</h4>
        <p>Once your app is registered and venues are created, check out the React Native SDK guide for implementation details.</p>
      </div>
    </div>
  );

  const renderReactNativeSDK = () => (
    <div className="integration-content">
      <h3>React Native SDK Integration</h3>
      <p>
        The PlatefullAI React Native SDK provides a complete authentication and onboarding solution with a single component.
        This guide covers installation, implementation, and customization.
      </p>

      <div className="info-section">
        <h4>Installation</h4>
        <p>Install the PlatefullAI React Native SDK and its required dependencies:</p>
        <div className="code-block">
          <pre><code>{`# Install the SDK package
npm install ./path/to/platefullai-react-native-sdk-1.0.1.tgz

# Install required dependencies
npm install axios @react-native-async-storage/async-storage react-native-url-polyfill react-native-webview`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Basic Implementation</h4>
        <p>
          Integration is simple! You only need a few lines of code. Here are the PlatefullAI-specific parts you'll add to your existing screen:
        </p>
        
        <div className="field-explanation">
          <h5>1. Import the SDK and Client ID</h5>
          <div className="code-block">
            <pre><code>{`import { SignInButton } from 'platefullai-react-native-sdk';
import { PLATEFULLAI_CLIENT_ID } from "../../config";`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>2. Add State (Optional - for tracking Diet ID status)</h5>
          <div className="code-block">
            <pre><code>{`const [dietIdActive, setDietIdActive] = useState(false);`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>3. Load Diet ID Status (Optional)</h5>
          <div className="code-block">
            <pre><code>{`useEffect(() => {
  loadDietIdStatus();
}, []);

const loadDietIdStatus = async () => {
  try {
    const status = await AsyncStorage.getItem('dietIdActive');
    if (status === 'true') {
      setDietIdActive(true);
    }
  } catch (error) {
    console.error('Error loading Diet ID status:', error);
  }
};`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>4. Handle Completion Callback</h5>
          <div className="code-block">
            <pre><code>{`const handleComplete = async (result) => {
  try {
    if (result.denied) {
      // User declined consent
      console.log('User declined Diet ID');
      setDietIdActive(false);
      await AsyncStorage.setItem('dietIdDenied', 'true');
      
    } else if (result.hasPreferences) {
      // Setup complete - user has preferences
      console.log('Diet ID activated!');
      setDietIdActive(true);
      await AsyncStorage.setItem('dietIdActive', 'true');
      await AsyncStorage.removeItem('dietIdDenied');
      
    } else {
      // User signed out
      console.log('User signed out of Diet ID');
      setDietIdActive(false);
      await AsyncStorage.removeItem('dietIdActive');
    }
  } catch (error) {
    console.error('Error saving Diet ID status:', error);
  }
};`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>5. Add the SignInButton to Your JSX</h5>
          <div className="code-block">
            <pre><code>{`{/* Add this wherever you want the button to appear */}
{userInfo?.user?.account?.email && (
  <View style={{
    alignItems: 'center',
    paddingVertical: 15,
    paddingHorizontal: 20,
  }}>
    <SignInButton
      userEmail={userInfo.user.account.email}
      clientId={PLATEFULLAI_CLIENT_ID}
      onComplete={handleComplete}
      onError={(error) => {
        console.error('Diet ID error:', error);
        Alert.alert('Diet ID Error', 'There was an issue with Diet ID. Please try again later.');
      }}
      theme={isDarkMode ? "dark" : "light"}
      size={isLargeScreen ? "large" : "medium"}
      hideOnComplete={true}  // Optional: Hide button after completion
    />
  </View>
)}`}</code></pre>
          </div>
        </div>

        <p style={{ marginTop: '16px' }}>
          <strong>That's it!</strong> The SDK handles everything else - authentication, dietary preferences, health concerns, 
          and consent flow. You just need to handle the result in your <code>handleComplete</code> callback.
        </p>
      </div>

      <div className="info-section">
        <h4>Complete Authentication Flow</h4>
        <p>The <code>SignInButton</code> handles the entire user journey automatically:</p>
        <ol>
          <li><strong>User Check:</strong> Verifies if user exists in PlatefullAI system</li>
          <li><strong>Authentication:</strong> Login/registration with email and password</li>
          <li><strong>Consent:</strong> Requests user permission for data sharing (if needed)</li>
          <li><strong>Dietary Preferences:</strong> User selects up to 3 dietary preferences</li>
          <li><strong>Health Concerns:</strong> User selects up to 3 health concerns</li>
          <li><strong>Completion:</strong> Returns user data and preferences</li>
        </ol>
      </div>

      <div className="info-section">
        <h4>Button States</h4>
        <p>The button automatically shows different states based on user status:</p>
        <div className="code-block">
          <pre><code>{`// New user - shows "Sign in with Platefull AI"
<SignInButton userEmail="newuser@example.com" clientId="your_client_id" />

// Authenticated user with no preferences - shows "Add your preferences"
<SignInButton userEmail="existing@example.com" clientId="your_client_id" />

// Authenticated user with preferences - shows "DIET ID ACTIVE" with logout option
<SignInButton userEmail="setup@example.com" clientId="your_client_id" />`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Response Data Structure</h4>
        <p>The <code>onComplete</code> callback receives different result objects based on the user's action:</p>
        <div className="code-block">
          <pre><code>{`const handleComplete = (result) => {
  if (result.denied) {
    // User declined consent
    console.log('User declined Diet ID');
    
  } else if (result.hasPreferences) {
    // User completed setup with preferences
    console.log('Access Token:', result.authentication.access_token);
    console.log('User Email:', result.authentication.user.email);
    console.log('Dietary Preferences:', result.dietaryPreferences);
    console.log('Health Concerns:', result.healthConcerns);
    
  } else {
    // User signed out
    console.log('User signed out of Diet ID');
  }
};`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Customization Options</h4>
        <p>The button supports multiple customization options:</p>
        
        <div className="field-explanation">
          <h5>Size Options</h5>
          <div className="code-block">
            <pre><code>{`<SignInButton size="small" />   // 200x40px
<SignInButton size="medium" />  // 280x50px (default)
<SignInButton size="large" />   // 360x60px`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>Theme Options</h5>
          <div className="code-block">
            <pre><code>{`<SignInButton theme="light" />  // Light theme (default)
<SignInButton theme="dark" />   // Dark theme`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>Custom Dimensions</h5>
          <div className="code-block">
            <pre><code>{`<SignInButton 
  width={250} 
  height={45} 
  borderRadius={12}
/>`}</code></pre>
          </div>
        </div>

        <div className="field-explanation">
          <h5>Hide on Complete</h5>
          <div className="code-block">
            <pre><code>{`<SignInButton 
  hideOnComplete={true}  // Hide button after user completes setup
/>`}</code></pre>
          </div>
          <p style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
            When <code>hideOnComplete</code> is true, the button will automatically hide once the user has completed 
            their dietary preferences setup. This provides a cleaner UX by removing the button after completion.
          </p>
        </div>

        <div className="field-explanation">
          <h5>Conditional Display</h5>
          <div className="code-block">
            <pre><code>{`<SignInButton 
  hide={!shouldShowButton}
/>`}</code></pre>
          </div>
        </div>
      </div>

      <div className="info-section">
        <h4>Error Handling</h4>
        <p>Implement robust error handling for different scenarios:</p>
        <div className="code-block">
          <pre><code>{`const handleError = (error) => {
  if (error.message.includes('Network')) {
    // Handle network issues
    Alert.alert('Connection Error', 'Please check your internet connection');
  } else if (error.message.includes('consent')) {
    // Handle consent denial
    console.log('User declined consent');
  } else {
    // Handle other errors
    Alert.alert('Error', error.message);
  }
};`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Complete Integration Example</h4>
        <p>Here's a complete example showing the SDK integrated into a profile screen:</p>
        <div className="code-block">
          <pre><code>{`import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { SignInButton } from 'platefullai-react-native-sdk';

export default function ProfileScreen() {
  const [userData, setUserData] = useState(null);
  const [showDietButton, setShowDietButton] = useState(true);

  const handleComplete = (result) => {
    if (result.success) {
      setUserData(result);
      setShowDietButton(false); // Hide button after successful setup
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Profile</Text>
      
      {userData && (
        <View style={styles.userInfo}>
          <Text>Email: {userData.authentication.user.email}</Text>
          <Text>Dietary Preferences: {userData.dietaryPreferences.join(', ')}</Text>
          <Text>Health Concerns: {userData.healthConcerns.join(', ')}</Text>
        </View>
      )}
      
      {showDietButton && (
        <SignInButton
          userEmail="user@example.com"
          clientId="your_client_id"
          onComplete={handleComplete}
          onError={(error) => console.error('SDK Error:', error)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  userInfo: {
    backgroundColor: '#f5f5f5',
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
  },
});`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>What's Next</h4>
        <p>
          The SDK currently handles user authentication and dietary preference setup. 
          Menu item processing functionality will be added in a future update, allowing you to:
        </p>
        <ul>
          <li>Process menu items with dietary classification</li>
          <li>Get personalized menu highlighting for users</li>
          <li>Display ingredient-level dietary information</li>
          <li>Show conflict and match indicators</li>
        </ul>
        <p>Stay tuned for updates to this documentation as new features are released.</p>
      </div>
      </div>
  );

  const renderVenueTracking = () => (
    <div className="integration-content">
      <h3>Venue Visit Tracking</h3>
      <p>
        Track user visits to your restaurant or platform using venue IDs. This enables personalized 
        recommendations and visit history for returning customers.
      </p>

      <div className="info-section">
        <h4>Overview</h4>
        <p>
          PlatefullAI supports two methods for tracking online visits:
        </p>
        <ul>
          <li><strong>Direct Venue ID</strong> - Use PlatefullAI's venue UUID (best for single restaurants)</li>
          <li><strong>Internal Venue ID</strong> - Use your own internal restaurant ID (best for platforms with multiple venues)</li>
        </ul>
      </div>

      <div className="info-section">
        <h4>Method 1: Direct Venue ID (Single Restaurant)</h4>
        <p>
          If you're a single restaurant, use your PlatefullAI venue ID directly. You can find this in 
          the <strong>Venues/Locations</strong> section of the developer portal.
        </p>
        <div className="code-block">
          <pre><code>{`PlatefullAI.renderSignInButton('platefullai-signin', {
  buttonText: 'Sign in with PlatefullAI',
  venueId: '123e4567-e89b-12d3-a456-426614174000', // Your venue UUID
  userEmail: localStorage.getItem('platefullai_user_email'),
  pulse: true,
  onSuccess: (user) => {
    localStorage.setItem('platefullai_user_email', user.email);
    window.location.reload();
  }
});`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Method 2: Internal Venue ID (Platforms)</h4>
        <p>
          If you're a platform (like DoorDash, UberEats) with many restaurants, use your own internal 
          restaurant IDs. This saves you from managing hundreds of UUIDs.
        </p>

        <div className="field-explanation">
          <h5>Step 1: Set Up Venue Mappings</h5>
          <p>
            Navigate to <strong>Venue ID Mappings</strong> in the developer portal and add your internal IDs 
            for each restaurant. For example:
          </p>
          <ul>
            <li>PlatefullAI Venue: "Joe's Pizza" → Internal ID: "doordash-restaurant-12345"</li>
            <li>PlatefullAI Venue: "Main Street Cafe" → Internal ID: "doordash-restaurant-67890"</li>
          </ul>
        </div>

        <div className="field-explanation">
          <h5>Step 2: Use Internal ID in Widget</h5>
          <p>Pass your internal venue ID instead of the PlatefullAI UUID:</p>
          <div className="code-block">
            <pre><code>{`// On your restaurant page, pass your internal restaurant ID
PlatefullAI.renderSignInButton('platefullai-signin', {
  buttonText: 'Sign in with PlatefullAI',
  internalVenueId: 'doordash-restaurant-12345', // Your internal ID
  userEmail: localStorage.getItem('platefullai_user_email'),
  pulse: true,
  onSuccess: (user) => {
    localStorage.setItem('platefullai_user_email', user.email);
    window.location.reload();
  }
});`}</code></pre>
          </div>
          <p>
            The widget automatically resolves your internal ID to the correct PlatefullAI venue 
            using your OAuth client credentials.
          </p>
        </div>

        <div className="field-explanation">
          <h5>Step 3: Bulk Upload (Optional)</h5>
          <p>
            For platforms with hundreds of restaurants, you can bulk upload your internal IDs via CSV. 
            Contact our support team for assistance with bulk operations.
          </p>
        </div>
      </div>

      <div className="info-section">
        <h4>Priority Logic</h4>
        <p>
          If both <code>venueId</code> and <code>internalVenueId</code> are provided, 
          <code>internalVenueId</code> takes priority. This ensures platforms can use their internal IDs 
          without conflicts.
        </p>
        <div className="code-block">
          <pre><code>{`// ✅ Good: Use one or the other
PlatefullAI.renderSignInButton('container', {
  internalVenueId: 'my-restaurant-123'  // Platform approach
});

// ✅ Also Good: Direct UUID
PlatefullAI.renderSignInButton('container', {
  venueId: '123e4567-e89b-12d3-a456-426614174000'  // Single restaurant
});

// ⚠️ Unnecessary: Both provided (internalVenueId will be used)
PlatefullAI.renderSignInButton('container', {
  internalVenueId: 'my-restaurant-123',  // This will be used
  venueId: '123e4567-...'                 // This will be ignored
});`}</code></pre>
        </div>
      </div>

      <div className="info-section">
        <h4>Benefits of Internal Venue IDs</h4>
        <ul>
          <li><strong>Scalability</strong> - Manage hundreds of restaurants without tracking UUIDs</li>
          <li><strong>Existing Systems</strong> - Use your current restaurant management IDs</li>
          <li><strong>No External Mapping</strong> - No need to maintain your own mapping table</li>
          <li><strong>Bulk Operations</strong> - Easily upload and manage multiple venues at once</li>
          <li><strong>Backward Compatible</strong> - Direct venue IDs continue to work</li>
        </ul>
      </div>

      <div className="info-section">
        <h4>What Happens Behind the Scenes</h4>
        <p>When a user signs in:</p>
        <ol>
          <li>Widget sends OAuth token + venue identifier to PlatefullAI</li>
          <li>If <code>internalVenueId</code> provided, PlatefullAI looks up the venue using your OAuth client</li>
          <li>If <code>venueId</code> provided, PlatefullAI uses it directly</li>
          <li>An "online visit" is created, linking the user to the venue</li>
          <li>User's dietary preferences are available for menu personalization</li>
          <li>Visit history is tracked for recommendations and analytics</li>
        </ol>
      </div>

      <div className="info-section">
        <h4>Security & Validation</h4>
        <ul>
          <li><strong>OAuth Scoped</strong> - Internal IDs are scoped to your OAuth client (no cross-platform access)</li>
          <li><strong>Unique Constraints</strong> - Each internal ID can only be used once per OAuth client</li>
          <li><strong>Owner Validation</strong> - Only the venue owner can create/update mappings</li>
          <li><strong>Access Control</strong> - User must be authenticated to create visits</li>
        </ul>
      </div>
    </div>
  );

  const renderDemoModal = () => {
    if (!showDemoModal) return null;

    const handleDemoClick = () => {
      if (demoStep < 4) {
        setDemoStep(demoStep + 1);
      } else {
        setShowDemoModal(false);
        setDemoStep(1);
      }
    };

    const handleCloseDemo = () => {
      setShowDemoModal(false);
      setDemoStep(1);
    };

    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={handleDemoClick}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCloseDemo();
          }}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontSize: '24px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          ×
        </button>

        <div
          style={{
            background: '#1e1e1e',
            borderRadius: '16px',
            maxWidth: '450px',
            width: '100%',
            minHeight: '570px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Step 1: Restaurant Website */}
          {demoStep === 1 && (
            <div style={{ padding: '40px 20px', cursor: 'pointer', minHeight: '570px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} onClick={handleDemoClick}>
              <div>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '16px',
                  borderRadius: '12px',
                  marginBottom: '30px',
                  color: 'white'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>🍽️ Restaurant Demo</h2>
                    <button style={{
                      background: 'white',
                      color: '#667eea',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                      Sign in with PlatefullAI
                    </button>
          </div>
          </div>
                
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <h1 style={{ fontSize: '32px', marginBottom: '16px', color: '#ffffff', fontWeight: '700' }}>Welcome to Our Restaurant</h1>
                  <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '30px' }}>
                    Click anywhere to see the sign-in flow
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '12px',
                    marginTop: '30px'
                  }}>
                    {['Menu', 'About', 'Contact', 'Reservations'].map((item) => (
                      <div key={item} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '20px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)',
                        transition: 'all 0.2s ease'
                      }}>
                        {item}
          </div>
                    ))}
          </div>
          </div>
          </div>
              
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.5)',
                marginTop: '20px'
              }}>
                💡 Click anywhere to continue the demo
          </div>
        </div>
          )}

          {/* Step 2: Auth Step */}
          {demoStep === 2 && (
            <div style={{ padding: '24px 20px', cursor: 'pointer', minHeight: '570px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} onClick={handleDemoClick}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <img src={PlatefullAILogo} alt="PlatefullAI" style={{ height: '12px', width: 'auto' }} />
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Account</h2>
      </div>

                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  <h2 style={{ fontSize: '24px', marginBottom: '8px', color: '#ffffff', fontWeight: '700' }}>Sign In</h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', margin: 0 }}>Welcome back! Sign in to your account</p>
      </div>

                <div style={{ maxWidth: '90%', margin: '0 auto' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#ffffff', fontSize: '14px' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="demo@example.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                        background: '#0a0a0a',
                        color: '#ffffff'
                      }}
                      value="demo@example.com"
                      readOnly
                    />
    </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#ffffff', fontSize: '14px' }}>
                      Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                        background: '#0a0a0a',
                        color: '#ffffff'
                      }}
                      value="password123"
                      readOnly
                    />
                  </div>

                  <button style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(90deg, #4ecdc4 0%, #ff6b6b 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                    Continue
                  </button>
                </div>
              </div>

              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.5)',
                marginTop: '20px'
              }}>
                💡 Click anywhere to continue
        </div>
      </div>
          )}

          {/* Step 3: Dietary Preferences */}
          {demoStep === 3 && (
            <div style={{ padding: '24px 20px', cursor: 'pointer', minHeight: '570px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} onClick={handleDemoClick}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <img src={PlatefullAILogo} alt="PlatefullAI" style={{ height: '12px', width: 'auto' }} />
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Dietary</h2>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 8px 0' }}>
                    Select up to 3 dietary preferences and restrictions
                  </p>
                </div>

                <div style={{ maxWidth: '100%', margin: '0 auto' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: '12px',
                    marginBottom: '24px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {['Vegan', 'Vegetarian', 'Gluten Allergy', 'Dairy Allergy', 'Nut Allergy', 'Keto'].map((pref, index) => (
                      <div key={pref} style={{
                        padding: '16px',
                        border: index < 2 ? '2px solid #4ecdc4' : '2px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        textAlign: 'left',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: index < 2 ? '#4ecdc4' : 'rgba(255, 255, 255, 0.7)',
                        background: index < 2 ? 'rgba(78, 205, 196, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <span>{pref}</span>
                        {index < 2 && (
                          <span style={{ fontSize: '16px' }}>✓</span>
                        )}
        </div>
                    ))}
      </div>

                  <button style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(90deg, #4ecdc4 0%, #ff6b6b 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                    Continue
                  </button>
        </div>
        </div>

              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.5)',
                marginTop: '20px'
              }}>
                💡 Click anywhere to continue
      </div>
            </div>
          )}

          {/* Step 4: Callback Page */}
          {demoStep === 4 && (
            <div style={{ padding: '24px 20px', minHeight: '570px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
                    borderRadius: '50%',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '40px',
                    color: 'white'
                  }}>
                    ✓
        </div>
                  <h2 style={{ fontSize: '24px', marginBottom: '8px', color: '#ffffff', fontWeight: '700' }}>Authentication Complete!</h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                    User has been redirected back to your callback URL
                  </p>
      </div>

                <div style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#ffffff', fontWeight: '600' }}>Callback URL:</h3>
                  <code style={{
                    display: 'block',
                    padding: '12px',
                    background: '#0a0a0a',
                    color: '#4ecdc4',
                    borderRadius: '6px',
                    fontSize: '12px',
                    overflow: 'auto',
                    marginBottom: '12px',
                    fontFamily: 'monospace',
                    wordWrap: 'break-word'
                  }}>
                    https://yourrestaurant.com/auth/platefullai/callback?code=abc123&amp;email=demo@example.com&amp;state=xyz
                  </code>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', margin: 0, lineHeight: '1.5' }}>
                    At this point, you should redirect the user to your desired page (e.g., back to the menu, their profile, etc.)
                  </p>
      </div>

                <div style={{
                  background: 'rgba(78, 205, 196, 0.1)',
                  border: '1px solid rgba(78, 205, 196, 0.3)',
                  padding: '16px',
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#4ecdc4', margin: '0 0 8px 0', fontWeight: '600' }}>
                    💡 What happens next?
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '20px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', lineHeight: '1.6' }}>
                    <li>The PlatefullAI widget automatically detects the callback and processes the authentication</li>
                    <li>User dietary data is fetched and stored locally</li>
                    <li>Your onSuccess callback is triggered</li>
                    <li>You redirect the user where you want (menu, profile, etc.)</li>
        </ul>
                </div>
              </div>

              <button
                onClick={handleCloseDemo}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(90deg, #4ecdc4 0%, #ff6b6b 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  marginTop: '20px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Close Demo
              </button>
            </div>
          )}
      </div>
    </div>
  );
  };

  return (
    <>
      {renderDemoModal()}
    <div className="documentation">
      <SectionHeader
        title="Documentation"
          description="Integration guides for PlatefullAI across different platforms"
      />

      <div className="documentation-layout">
        <div className="integration-sidebar">
          <div className="sidebar-section">
            <h3>Overview</h3>
            <div className="integration-list">
              {overviewSections.map((section) => (
                <button
                  key={section.id}
                  className={`integration-item ${selectedSection === section.id ? 'active' : ''}`}
                  onClick={() => setSelectedSection(section.id)}
                >
                  <div className="integration-name">{section.name}</div>
                  <div className="integration-description">{section.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Web Integrations</h3>
            <div className="integration-list">
              {webIntegrationSections.map((section) => (
                <button
                  key={section.id}
                  className={`integration-item ${selectedSection === section.id ? 'active' : ''}`}
                  onClick={() => setSelectedSection(section.id)}
                >
                  <div className="integration-name">{section.name}</div>
                  <div className="integration-description">{section.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Mobile Integrations</h3>
            <div className="integration-list">
              {mobileIntegrationSections.map((section) => (
                <button
                  key={section.id}
                  className={`integration-item ${selectedSection === section.id ? 'active' : ''}`}
                  onClick={() => setSelectedSection(section.id)}
                >
                  <div className="integration-name">{section.name}</div>
                  <div className="integration-description">{section.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="integration-main">
          {selectedSection === 'overview' && renderOverview()}
          {selectedSection === 'css-classes' && renderCSSClasses()}
          {selectedSection === 'web-getting-started' && renderWebGettingStarted()}
          {selectedSection === 'web-html-integration' && renderWebHTMLIntegration()}
          {selectedSection === 'venue-tracking' && renderVenueTracking()}
          {selectedSection === 'mobile-getting-started' && renderMobileGettingStarted()}
          {selectedSection === 'react-native-sdk' && renderReactNativeSDK()}
        </div>
      </div>
    </div>
    </>
  );
};

export default Documentation;

